import {ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewContainerRef} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {PaymentError} from '@app/payment/services/error-handling/payment-error.enum';
import {DcpCartService, PO_NUMBER_ACTIONS} from '@app/product/services/dcp-cart.service';
import {DcpActiveCartService} from '@app/services/dcp-active-cart.service';
import {DcpCartDecoratorService} from '@app/services/dcp-cart-decorator.service';
import {ORDER_REVIEW_ACCORDION_LOCALSTORAGE_KEY_PREFIX} from '@app/services/local-storage.constants';
import {TermsConditionsService} from '@app/services/terms-conditions.service';
import {TransactionTypeService} from '@app/services/transactionTypes/transaction-type.service';
import {DcpGlobalMessageService, LocalStorageService} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Cart} from '@spartacus/cart/base/root';
import {OrderEntry} from '@spartacus/cart/base/root/models/cart.model';
import {CheckoutPlaceOrderComponent} from '@spartacus/checkout/base/components';
import {RoutingService} from '@spartacus/core';
import {OrderFacade} from '@spartacus/order/root';
import {LaunchDialogService} from '@spartacus/storefront';
import {combineLatest, Observable, of} from 'rxjs';
import {catchError, first, map, switchMap} from 'rxjs/operators';
import {CartDataService} from '../services/cart-data.service';
import {CartDecoratorErrorHandlingService} from '../services/cart-decorator-error-handling.service';
import {CheckoutEntryValidationService} from '../services/checkout-entry-validation.service';
import {ThreeDsService} from '@app/services/three-ds/three-ds.service';

@UntilDestroy()
@Component({
  selector: 'dcp-checkout-place-order',
  templateUrl: './dcp-checkout-place-order.component.html',
  styleUrls: ['./dcp-checkout-place-order.component.scss']
})
export class DcpCheckoutPlaceOrderComponent extends CheckoutPlaceOrderComponent implements OnInit, OnDestroy {

  @Input() entries: OrderEntry[];
  @Input() cartIsLoading: boolean;

  requestPending: boolean = false;
  poSaveRequestPending: boolean = false;
  checkoutSubmitForm: UntypedFormGroup = this.fb.group({
    termsAndConditions: [true, Validators.requiredTrue],
  });
  isTermsConditionsLinkShown$ = of(true);

  taxError: boolean = false;
  isCartInvalid: boolean = false;
  paymentError: boolean = false;
  placeOrderDisabled: boolean = false;

  constructor(
    protected activeCartService: DcpActiveCartService,
    protected cartDecoratorErrorHandlingService: CartDecoratorErrorHandlingService,
    protected orderFacade: OrderFacade,
    protected checkoutEntryValidationService: CheckoutEntryValidationService,
    protected termsConditionsService: TermsConditionsService,
    protected fb: UntypedFormBuilder,
    protected routingService: RoutingService,
    protected launchDialogService: LaunchDialogService,
    protected localStorageService: LocalStorageService,
    protected vcr: ViewContainerRef,
    private cartDecorator: DcpCartDecoratorService,
    private changeDetectorRef: ChangeDetectorRef,
    private cartService: DcpCartService,
    private transactionTypeService: TransactionTypeService,
    private dcpGlobalMessageService: DcpGlobalMessageService,
    private cartDataService: CartDataService,
    private cdr: ChangeDetectorRef,
    private threeDsService: ThreeDsService,
  ) {
    super(orderFacade, routingService, fb, launchDialogService, vcr);
  }

  ngOnInit() {
    this.subscribeToPoNumberSubmit();
    this.subscribeToPlaceOrderPending();
    this.subscribeToDisabledPlaceOrderButton();
    this.checkoutEntryValidationService.isActiveCartInvalid().pipe(untilDestroyed(this)).subscribe(isInvalid => {
      this.isCartInvalid = isInvalid;
      this.changeDetectorRef.detectChanges();
    });
    this.isTermsConditionsLinkShown$ = this.getIsTermsConditionsLinkShown(this.entries);
    this.findTaxErrorInCart();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.threeDsService.cancelThreeDs();
  }

  /**
   * Communicates to the po number component to that the number should be stored in the backend.
   */
  checkPoNumberAndSubmitForm(): void {
    this.cartService.emitPoNumberMessage(PO_NUMBER_ACTIONS.sendPoNumber);
  }

  /**
   * Subscribes to cartService.poNumberSubmit to get informed when the number was stored successfully.
   */
  subscribeToPoNumberSubmit() {
    this.cartService.getPoNumberMessage$().pipe(untilDestroyed(this)).subscribe(message => {
      this.reactToPoNumberSubmitEvents(message);
    });
  }

  public reactToPoNumberSubmitEvents(message: any) {
    switch (message) {
      case PO_NUMBER_ACTIONS.poNumberCurrentSaveRequestFinishedSuccessfully:
      case PO_NUMBER_ACTIONS.poNumberCurrentSaveRequestFinishedWithErrors:
        this.poSaveRequestPending = false;
        this.changeDetectorRef.detectChanges();
        break;
      case PO_NUMBER_ACTIONS.poNumberCurrentSaveRequestPending:
        this.changeDetectorRef.detectChanges();
        this.poSaveRequestPending = true;
        break;
      case PO_NUMBER_ACTIONS.placeOrder:
        if (!this.poSaveRequestPending) {
          this.submitForm();
          this.changeDetectorRef.detectChanges();
        }
        break;
    }
  }

  submitForm(): void {
    if (this.checkoutSubmitForm.valid) {
      this.activeCartService.getActiveCart().pipe(first(), untilDestroyed(this)).subscribe((cart: Cart) => {
        this.cartDecorator.startPaymentFlow(cart.purchaseOrderNumber || '').pipe(untilDestroyed(this)).subscribe(() => {
            setTimeout(() => {
              this.localStorageService.removeLocalStorageItemsByPrefix(
                ORDER_REVIEW_ACCORDION_LOCALSTORAGE_KEY_PREFIX
              );
              this.changeDetectorRef.detectChanges();
            }, 0);
          },
          (error) => {
            if (error === PaymentError) {
              this.paymentError = true;
            }
            this.cartDecoratorErrorHandlingService.handle(error, null);
          });
      });
    } else {
      this.checkoutSubmitForm.markAllAsTouched();
    }
  }

  isPlaceOrderButtonDisabled(): boolean {
    return this.requestPending || this.poSaveRequestPending || this.isCartInvalid || this.cartIsLoading || this.paymentError || this.taxError || this.placeOrderDisabled;
  }

  /**
   * Checks if any NetNew product has no TermsConditions and sets isTermsConditionsLinkShown to false if that's the case.
   * isTermsConditionsLinkShown is set to true if OrderEntry contains a Partner Product or if the
   * getTermsConditionsByEntryProduct method returns Terms&Conditions for the NetNew product.
   *
   * @param entries List of all OrderEntries
   */
  getIsTermsConditionsLinkShown(entries: OrderEntry[]): Observable<boolean> {
    const doEntriesHaveTerms$: Observable<boolean>[] = [];
    entries.forEach(entry => {
      doEntriesHaveTerms$.push(
        this.transactionTypeService.isNetNew(entry.transactionType)
          .pipe(first(),
            switchMap(isNetNew => {
                if (isNetNew) {
                  return this.termsConditionsService.getTermsConditionsByEntryProduct(entry)
                    .pipe(first(),
                      map((t) => !t?.error),
                      catchError((e) => {
                        this.dcpGlobalMessageService.addMessage(e, this?.constructor?.name, 'errorLogging');
                        return of(false);
                      }),
                      untilDestroyed(this)
                    );
                }
                return of(true);
              }
            ),
            untilDestroyed(this)
          )
      );
    });
    return combineLatest(doEntriesHaveTerms$).pipe(map(doEntriesHaveTerms => doEntriesHaveTerms.some(eht => eht)));
  }

  findTaxErrorInCart(): void {
    this.activeCartService.getActiveCart().pipe(untilDestroyed(this)).subscribe((cart) => {
      this.taxError = (this.cartDataService.getErrorDetails(cart)?.severity === 'error');
    });
  }

  private subscribeToPlaceOrderPending() {
    this.cartDecorator.isPlaceOrderPending$.pipe(untilDestroyed(this)).subscribe((isPlaceOrderPending) => {
      this.requestPending = isPlaceOrderPending;
      setTimeout(() => {
        this.cdr.detectChanges();
      }, 100);
    });
  }

  private subscribeToDisabledPlaceOrderButton() {
    this.cartDecoratorErrorHandlingService.disablePlaceOrder$.pipe(untilDestroyed(this)).subscribe(value => {
      this.placeOrderDisabled = value;
    });
  }

  clearLastPage() {
    this.localStorageService.removeItem('lastPage');
  }
}
